import type QRCodeStyling from 'qr-code-styling';
import type { TFileExtension, TQrCodeOptions } from '@shared/schemas';

function escapeXml(value: string): string {
	return value.replace(/[<>&"']/g, (character) => {
		const replacements: Record<string, string> = {
			'<': '&lt;',
			'>': '&gt;',
			'&': '&amp;',
			'"': '&quot;',
			"'": '&apos;',
		};
		return replacements[character] ?? character;
	});
}

function downloadBlob(blob: Blob, fileName: string) {
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.download = fileName;
	document.body.append(anchor);
	anchor.click();
	anchor.remove();
	window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function buildFramedSvg(svg: string, config: TQrCodeOptions): Blob {
	const source = svg.replace(/^\s*<\?xml[^>]*\?>\s*/i, '');
	const match = source.match(/<svg\b[^>]*>([\s\S]*?)<\/svg>/i);
	if (!match?.[1]) throw new Error('Unable to prepare the framed QR code image');
	const width = config.width;
	const height = config.height;
	const outerWidth = width + 40;
	const outerHeight = height + 108;
	const style = config.frameOptions.style;
	const text = escapeXml(config.frameOptions.text.trim().slice(0, 24) || 'Scan me');
	const frame =
		style === 'badge'
			? `<rect x="20" y="${height + 38}" width="${width}" height="46" rx="8" fill="#0f172a"/><text x="${outerWidth / 2}" y="${height + 67}" text-anchor="middle" font-family="Arial,sans-serif" font-size="20" font-weight="700" fill="#ffffff">${text}</text>`
			: `<text x="${outerWidth / 2}" y="${height + 68}" text-anchor="middle" font-family="Arial,sans-serif" font-size="20" font-weight="700" fill="#0f172a">${text}</text>`;
	const outline =
		style === 'outline'
			? '<rect x="2" y="2" width="99.6%" height="99.6%" rx="18" fill="none" stroke="#0f172a" stroke-width="4"/>'
			: '';
	const framed = `<svg xmlns="http://www.w3.org/2000/svg" width="${outerWidth}" height="${outerHeight}" viewBox="0 0 ${outerWidth} ${outerHeight}"><rect width="100%" height="100%" fill="#ffffff"/><svg x="20" y="20" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${match[1]}</svg>${frame}${outline}</svg>`;
	return new Blob([framed], { type: 'image/svg+xml;charset=utf-8' });
}

async function svgToImageBlob(svg: Blob, extension: Exclude<TFileExtension, 'svg'>): Promise<Blob> {
	const sourceUrl = URL.createObjectURL(svg);
	try {
		const image = new Image();
		image.decoding = 'async';
		image.src = sourceUrl;
		await image.decode();
		const scale = extension === 'jpeg' ? 1 : 2;
		const canvas = document.createElement('canvas');
		canvas.width = image.naturalWidth * scale;
		canvas.height = image.naturalHeight * scale;
		const context = canvas.getContext('2d');
		if (!context) throw new Error('Unable to create the QR image');
		context.fillStyle = '#ffffff';
		context.fillRect(0, 0, canvas.width, canvas.height);
		context.drawImage(image, 0, 0, canvas.width, canvas.height);
		const mime = extension === 'jpeg' ? 'image/jpeg' : `image/${extension}`;
		const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mime, 0.95));
		if (!blob) throw new Error('Unable to export the QR image');
		return blob;
	} finally {
		URL.revokeObjectURL(sourceUrl);
	}
}

export async function downloadQrCodeWithFrame(
	instance: QRCodeStyling,
	config: TQrCodeOptions,
	fileName: string,
	extension: TFileExtension,
): Promise<void> {
	const safeFileName =
		fileName.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '-').slice(0, 120) || 'qr-code';
	if (config.frameOptions.style === 'none') {
		await instance.download({ name: safeFileName, extension });
		return;
	}
	const rawSvg = await instance.getRawData('svg');
	if (!rawSvg) throw new Error('Unable to render the QR code');
	const svgText = rawSvg instanceof Blob ? await rawSvg.text() : new TextDecoder().decode(rawSvg);
	const framedSvg = buildFramedSvg(svgText, config);
	const blob = extension === 'svg' ? framedSvg : await svgToImageBlob(framedSvg, extension);
	const mimeExtension = extension === 'jpeg' ? 'jpg' : extension;
	downloadBlob(blob, `${safeFileName}.${mimeExtension}`);
}
